import { useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import FormGroup from '@mui/material/FormGroup';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogContentText from '@mui/material/DialogContentText';

import { useTranslate } from 'src/locales';
import { WriteBackConflict, WriteBackFieldKey } from 'src/services/candidates/candidates.types';

import { WRITE_BACK_FIELD_LABEL_KEY, formatWriteBackValue } from './write-back.utils';

type Props = {
  open: boolean;
  conflicts: WriteBackConflict[];
  loading?: boolean;
  onConfirm: (fields: WriteBackFieldKey[]) => void;
  onCancel: () => void;
};

// JT1-1534: per-field confirm for flowing an application edit back to the recruiter's CRM record.
// Each differing field is a toggle (defaulted ON); confirming writes only the toggled-on fields.
export default function WriteBackConfirmDialog({ open, conflicts, loading, onConfirm, onCancel }: Props) {
  const { t } = useTranslate();
  const [selected, setSelected] = useState<WriteBackFieldKey[]>([]);

  useEffect(() => {
    // whenever a fresh set of conflicts arrives, default them all to ON
    if (open) setSelected(conflicts.map((conflict) => conflict.field));
  }, [open, conflicts]);

  const toggle = (field: WriteBackFieldKey) => {
    setSelected((prev) => (prev.includes(field) ? prev.filter((item) => item !== field) : [...prev, field]));
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('profile.write_back.title')}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('profile.write_back.subtitle')}</DialogContentText>

        <FormGroup>
          {conflicts.map((conflict) => (
            <FormControlLabel
              key={conflict.field}
              sx={{ alignItems: 'flex-start', mb: 1 }}
              control={
                <Checkbox
                  checked={selected.includes(conflict.field)}
                  onChange={() => toggle(conflict.field)}
                />
              }
              label={
                <Stack sx={{ pt: 1 }}>
                  <Typography variant="subtitle2">{t(WRITE_BACK_FIELD_LABEL_KEY[conflict.field])}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {`${t('profile.write_back.current')}: ${formatWriteBackValue(conflict.field, conflict.crm_value, t)}`}
                    {'  →  '}
                    {`${t('profile.write_back.new')}: ${formatWriteBackValue(conflict.field, conflict.new_value, t)}`}
                  </Typography>
                </Stack>
              }
            />
          ))}
        </FormGroup>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onCancel} disabled={loading}>
          {t('profile.write_back.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={selected.length === 0}
          onClick={() => onConfirm(selected)}
        >
          {t('profile.write_back.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
